import React, { useState, useMemo, useEffect } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";

function EasyCalender({ onSelect, selected }) {
	// State to control the month displayed by Calendar.
	// Initialize with the month of the selected date, or the current month if no date is selected.
	const [displayMonth, setDisplayMonth] = useState(
		selected ? new Date(selected.getFullYear(), selected.getMonth(), 1) : new Date()
	);

	// Effect to keep displayMonth in sync if the 'selected' prop changes externally.
	useEffect(() => {
		if (selected) {
			const selectedMonthStart = new Date(selected.getFullYear(), selected.getMonth(), 1);
			// Only update if the month is actually different to avoid unnecessary re-renders
			if (displayMonth.getTime() !== selectedMonthStart.getTime()) {
				setDisplayMonth(selectedMonthStart);
			}
		} else {
			// If selected becomes null/undefined, reset displayMonth to current month
			setDisplayMonth(new Date());
		}
	}, [selected, displayMonth]); // Dependency on 'selected' prop and displayMonth for comparison

	const currentYear = displayMonth.getFullYear();
	const currentMonthIndex = displayMonth.getMonth(); // 0-indexed

	// Generate options for years based on a child being 2 to 14 years old now
	const yearOptions = useMemo(() => {
		const years = [];
		const currentYear = new Date().getFullYear();
		// A child who is 14 years old now was born in currentYear - 14
		const startYear = currentYear - 14;
		// A child who is 2 years old now was born in currentYear - 2
		const endYear = currentYear - 2;
		for (let i = startYear; i <= endYear; i++) {
			years.push(i);
		}
		return years;
	}, []);

	// Month names for the selector
	const monthNames = useMemo(() => [
		"January", "February", "March", "April", "May", "June",
		"July", "August", "September", "October", "November", "December"
	], []);

	// Handler for when the month selector changes
	const handleMonthChange = (value) => { // Changed to directly receive value
		const newMonthIndex = parseInt(value, 10);
		const newDate = new Date(currentYear, newMonthIndex, 1); // First day of the new month
		setDisplayMonth(newDate); // Update the calendar's displayed month
		onSelect(newDate); // Update the selected date to the first day of the new month
	};

	// Handler for when the year selector changes
	const handleYearChange = (value) => { // Changed to directly receive value
		const newYear = parseInt(value, 10);
		const newDate = new Date(newYear, currentMonthIndex, 1); // First day of the new year/month
		setDisplayMonth(newDate); // Update the calendar's displayed month
		onSelect(newDate); // Update the selected date to the first day of the new year/month
	};

	// Handler for when a date is selected in the Calendar component
	const handleCalendarSelect = (date) => {
		if (date) {
			onSelect(date); // Call the parent's onSelect with the newly selected date
			// Also update displayMonth if the selected date is in a different month
			const selectedMonthStart = new Date(date.getFullYear(), date.getMonth(), 1);
			if (displayMonth.getTime() !== selectedMonthStart.getTime()) {
				setDisplayMonth(selectedMonthStart);
			}
		} else {
			onSelect(null); // Allow clearing selection
		}
	};

	return (
		<div>
			<div className="flex gap-2 p-2">
				{/* Month Selector */}
				<Select value={currentMonthIndex.toString()} onValueChange={handleMonthChange}> {/* Simplified onValueChange */}
					<SelectTrigger className="flex-grow">
						<SelectValue placeholder="Select a month" />
					</SelectTrigger>
					<SelectContent>
						{monthNames.map((name, index) => (
							<SelectItem key={index} value={index.toString()}>
								{name}
							</SelectItem>
						))}
					</SelectContent>
				</Select>

				{/* Year Selector */}
				<Select value={currentYear.toString()} onValueChange={handleYearChange}> {/* Simplified onValueChange */}
					<SelectTrigger className="flex-grow">
						<SelectValue placeholder="Select a year" />
					</SelectTrigger>
					<SelectContent>
						{yearOptions.map((year) => (
							<SelectItem key={year} value={year.toString()}>
								{year}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			</div>
			<Calendar
				mode="single"
				month={displayMonth} // Control the displayed month
				onMonthChange={setDisplayMonth} // Allow calendar to change its displayed month
				selected={selected}
				onSelect={handleCalendarSelect} // Use the new handler
				className={`rounded-lg border shadow-sm`} />
		</div>
	);
}

export default EasyCalender;